/**
 * Prominent pending-invitations banner shown at the top of the app shell (not
 * buried in the org switcher). Neon has no direct add-member, so an existing user
 * joins an org by accepting an invitation — this is where they see and accept it,
 * with the organization, inviter, and role spelled out.
 *
 * Renders nothing when there are no pending invitations.
 */
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Mail } from 'lucide-react'
import { useMyInvitations } from '../hooks/useMyInvitations'
import { Button } from './ui/button'
import { TESTID } from '../lib/testids'

export function InvitationsBanner() {
  const { t } = useTranslation()
  const { query, accept, reject } = useMyInvitations()
  const invites = query.data ?? []

  if (invites.length === 0) return null

  const busy = accept.isPending || reject.isPending

  const roleLabel = (r: string) =>
    t(`organizations.role.${r}`, { defaultValue: r })

  const onAccept = (invitationId: string, organizationId: string) =>
    accept.mutate(
      { invitationId, organizationId },
      {
        onSuccess: () => toast.success(t('organizations.onboarding.accepted')),
        onError: () => toast.error(t('organizations.toast.error')),
      },
    )

  const onReject = (invitationId: string) =>
    reject.mutate(invitationId, {
      onSuccess: () => toast.success(t('organizations.invitations.rejected')),
      onError: () => toast.error(t('organizations.toast.error')),
    })

  return (
    <div
      className="border-b border-warning/30 bg-warning/10 px-4 py-3 md:px-6"
      data-testid={TESTID.invitationsBanner}
    >
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
        <Mail className="size-4 shrink-0 text-warning" />
        {t('organizations.invitations.heading')}
      </div>
      <ul className="space-y-2">
        {invites.map((inv) => (
          <li
            key={inv.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2"
            data-testid={TESTID.invitationBannerRow}
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-foreground">
                {inv.organizationName ?? inv.organizationId}
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {t('organizations.invitations.meta', {
                  role: roleLabel(inv.role),
                  inviter:
                    inv.inviterName ??
                    inv.inviterEmail ??
                    t('organizations.invitations.unknownInviter'),
                })}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                data-testid={TESTID.invitationBannerReject(inv.id)}
                onClick={() => onReject(inv.id)}
              >
                {t('organizations.invitations.reject')}
              </Button>
              <Button
                size="sm"
                disabled={busy}
                data-testid={TESTID.invitationBannerAccept(inv.id)}
                onClick={() => onAccept(inv.id, inv.organizationId)}
              >
                {t('organizations.invitations.accept')}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
